import React, { useEffect, useMemo } from "react";
import Select from "react-select";
import { useQuery } from "@tanstack/react-query";
import { fetchPaymentMode } from "services/dropdown";
import { DROPDOWN_STALE_TIME } from "utils/constants";
import { Controller } from "react-hook-form";
import { useDispatch } from "react-redux";

const PaymentModeDropdown = ({
  errors,
  control,
  setValue,
  getValues,
  watch,
  rowData,
  required,
  online_mode = false,
  defaultValue,
}) => {
  const dispatch = useDispatch();

  const paymentModeName =
    rowData?.payment_mode_obj?.payment_mode ||
    rowData?.payment_mode ||
    getValues()?.payment_mode;

  const paymentModeId =
    rowData?.payment_mode_obj?.id ||
    rowData?.payment_mode_id ||
    getValues()?.payment_mode_id ||
    defaultValue;

  const selectedPaymentMode = watch("payment_mode_id");

  const {
    data: paymentModes,
    isLoading,
    error,
  } = useQuery(["PaymentModes"], fetchPaymentMode, {
    staleTime: DROPDOWN_STALE_TIME,
  });

  const options = useMemo(() => {
    return (
      paymentModes?.data?.data?.map((mode) => ({
        value: mode.id,
        label: mode.payment_mode,
      })) || []
    );
  }, [paymentModes]);

  const onlineOption = useMemo(() => {
    return options.find((option) => option.label.toLowerCase() === "online");
  }, [options]);

  const selectedOption = useMemo(() => {
    if (online_mode && onlineOption) {
      return onlineOption;
    }

    return (
      options.find((option) => option.value === selectedPaymentMode) ||
      options.find((option) => option.value === paymentModeId) ||
      options.find((option) => option.label === paymentModeName) ||
      null
    );
  }, [
    options,
    selectedPaymentMode,
    paymentModeId,
    paymentModeName,
    online_mode,
    onlineOption,
  ]);

  useEffect(() => {
    if (online_mode && onlineOption && options.length > 0) {
      setValue("payment_mode_id", onlineOption.value);
      setValue("payment_mode", onlineOption.label);
      return;
    }

    if (rowData && options.length) {
      const rowDataOption = options.find(
        (option) =>
          option.value === rowData?.payment_mode_obj?.id ||
          option.value === rowData?.payment_mode_id
      );
      if (rowDataOption) {
        setValue("payment_mode_id", rowDataOption.value);
        setValue("payment_mode", rowDataOption.label);
      }
    }
  }, [rowData, options, setValue, online_mode, onlineOption]);

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error loading Payment Modes</div>;

  return (
    <Controller
      name="payment_mode_id"
      control={control}
      rules={{
        required: required ? "Payment Mode is required" : false,
        validate: (value) => {
          if (required && !value) return "Payment Mode is required";
          return true;
        },
      }}
      render={({ field }) => (
        <Select
          {...field}
          options={options}
          isClearable={!online_mode}
          isDisabled={online_mode}
          value={selectedOption || null}
          placeholder="Select Payment Mode"
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          }}
          onChange={(selectedOption) => {
            if (!online_mode) {
              const value = selectedOption ? selectedOption.value : "";
              field.onChange(value);
              setValue("payment_mode_id", value);
              setValue(
                "payment_mode",
                selectedOption ? selectedOption.label : ""
              );
            }
          }}
          className={`react-select-container ${
            errors.payment_mode_id ? "is-invalid" : ""
          }`}
        />
      )}
    />
  );
};

export default PaymentModeDropdown;
